// Prop-driven planner question components, extracted verbatim from
// TripPlannerWizard.tsx so the exact same controls (markup, styling, option
// sets, and the signed-in ability collapse/expand behaviour) can be reused
// by a future Adjust Preferences editor without duplicating any of this JSX
// a second time. Pure presentation + a `value`/`onChange` contract -- no
// planner state, no analytics, no RPC calls live here; those stay in
// TripPlannerWizard.tsx.
//
// Seven questions, four distinct shapes:
//   - AbilityQuestion: bespoke. The only question with real behavioural
//     state (loading / signed-in summary / full picker), so it doesn't fit
//     a shared options-grid shape and isn't forced into one.
//   - SurfGoalQuestion, TripVibeQuestion, CrowdToleranceQuestion,
//     WaterTempQuestion: identical shape today (a visible radio icon next
//     to a text label, in a simple grid) -- these four share the internal
//     RadioOptionGrid helper below rather than each re-implementing the
//     same markup.
//   - MonthQuestion, BudgetQuestion: each bespoke. Both use a visually
//     centred chip with a screen-reader-only radio input, but differ from
//     each other in padding, text sizing, and single- vs multi-line content
//     -- unifying them with SurfGoalQuestion's shape (or with each other)
//     would need enough extra style-variant props to stop being the
//     smallest reusable abstraction, so they stay separate, single-purpose
//     components instead.
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import {
  SKILL_LEVEL_VALUES,
  BUDGET_VALUES,
  type SkillLevel,
  type SurfGoal,
  type TripVibe,
  type Budget,
  type WaterTemp,
  type CrowdTolerance,
} from '@/lib/planner-vocabulary';
import {
  MONTHS,
  SURF_GOAL_CHOICES,
  TRIP_VIBE_OPTIONS,
  CROWD_TOLERANCE_OPTIONS,
  WATER_TEMP_OPTIONS,
} from '@/lib/planner-display';

const optionClass = (active: boolean) =>
  cn(
    'flex items-center gap-2 p-2.5 sm:p-4 rounded-lg border cursor-pointer transition-all text-xs sm:text-sm',
    active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
  );

interface RadioOptionGridProps<T extends string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  gridClassName: string;
}

// Internal only -- not exported. The one shape that's genuinely identical
// across four questions today (see this file's header comment); everything
// else stays its own bespoke component rather than being bent to fit this.
function RadioOptionGrid<T extends string>({ label, value, onChange, options, gridClassName }: RadioOptionGridProps<T>) {
  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={(v) => onChange(v as T)} className={gridClassName}>
        {options.map((o) => (
          <Label key={o.value} className={optionClass(value === o.value)}>
            <RadioGroupItem value={o.value} className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{o.label}</span>
          </Label>
        ))}
      </RadioGroup>
    </div>
  );
}

interface AbilityQuestionProps {
  /** False while auth is still initialising, or a signed-in user's stored
   * surf_level query is still in flight -- see TripPlannerWizard.tsx's
   * abilityResolved for the exact derivation. */
  abilityResolved: boolean;
  /** True only when resolved, the user is signed in with a known stored
   * level, and they haven't tapped "Change" this session -- i.e. the exact
   * moment to show the collapsed summary instead of the full picker. */
  showSummary: boolean;
  /** The signed-in user's stored surf_level, already mapped onto this
   * planner's 4-value scale -- only read when showSummary is true. */
  profileSkillLevel: SkillLevel | null;
  onRequestChange: () => void;
  value: SkillLevel;
  onChange: (level: SkillLevel) => void;
}

// This is the highest-stakes question in the planner -- getting it wrong
// mismatches a surfer with water they aren't ready for -- but the options
// themselves (beginner/intermediate/advanced/expert) are self-explanatory
// enough that no further copy is needed per option; a prior version added
// one-line hints here, which in practice just made this question read as
// disproportionately large next to the plainer options elsewhere on the
// same screen. Removed rather than shortened.
export const AbilityQuestion = ({
  abilityResolved,
  showSummary,
  profileSkillLevel,
  onRequestChange,
  value,
  onChange,
}: AbilityQuestionProps) => (
  <div className="space-y-3">
    <Label>What best describes your surfing?</Label>
    {!abilityResolved ? (
      <div
        data-testid="ability-loading-placeholder"
        aria-hidden="true"
        className="h-[52px] sm:h-[58px] rounded-lg border border-border bg-muted/30 animate-pulse"
      />
    ) : showSummary ? (
      <div className="flex items-center justify-between gap-3 p-3 sm:p-3.5 rounded-lg border border-border bg-muted/30">
        <span className="text-xs sm:text-sm font-medium">
          Your level: <span className="capitalize">{profileSkillLevel}</span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto py-1 px-2 text-xs sm:text-sm shrink-0"
          onClick={onRequestChange}
        >
          Change
        </Button>
      </div>
    ) : (
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as SkillLevel)}
        className="grid grid-cols-1 gap-2 sm:gap-2.5"
      >
        {SKILL_LEVEL_VALUES.map((level) => (
          <Label
            key={level}
            className={cn(
              'flex items-start gap-3 p-3 sm:p-3.5 rounded-lg border cursor-pointer transition-all',
              value === level ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
            )}
          >
            <RadioGroupItem value={level} className="mt-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
            <span className="text-xs sm:text-sm font-medium capitalize">{level}</span>
          </Label>
        ))}
      </RadioGroup>
    )}
  </div>
);

interface SurfGoalQuestionProps {
  value: SurfGoal;
  onChange: (value: SurfGoal) => void;
}

// Three choices, not four -- 'consistent' ("Surf More Often") is retained in
// the internal SurfGoal vocabulary and in SURF_GOAL_OPTIONS's label lookup
// (see planner-display.ts) for backward compatibility, but is deliberately
// left out of SURF_GOAL_CHOICES, so it never appears as something a user can
// newly select here.
export const SurfGoalQuestion = ({ value, onChange }: SurfGoalQuestionProps) => (
  <RadioOptionGrid
    label="What do you want to get out of your surfing?"
    value={value}
    onChange={onChange}
    gridClassName="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"
    options={SURF_GOAL_CHOICES}
  />
);

interface TripVibeQuestionProps {
  value: TripVibe;
  onChange: (value: TripVibe) => void;
}

// The atmosphere around the surfing -- independent of surf goal above.
export const TripVibeQuestion = ({ value, onChange }: TripVibeQuestionProps) => (
  <RadioOptionGrid
    label="What kind of trip do you want?"
    value={value}
    onChange={onChange}
    gridClassName="grid grid-cols-2 gap-2 sm:gap-3"
    options={TRIP_VIBE_OPTIONS}
  />
);

interface CrowdToleranceQuestionProps {
  value: CrowdTolerance;
  onChange: (value: CrowdTolerance) => void;
}

export const CrowdToleranceQuestion = ({ value, onChange }: CrowdToleranceQuestionProps) => (
  <RadioOptionGrid
    label="How much do crowds bother you?"
    value={value}
    onChange={onChange}
    gridClassName="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"
    options={CROWD_TOLERANCE_OPTIONS}
  />
);

interface WaterTempQuestionProps {
  value: WaterTemp;
  onChange: (value: WaterTemp) => void;
}

export const WaterTempQuestion = ({ value, onChange }: WaterTempQuestionProps) => (
  <RadioOptionGrid
    label="Water temperature preference"
    value={value}
    onChange={onChange}
    gridClassName="grid grid-cols-2 gap-2 sm:gap-3"
    options={WATER_TEMP_OPTIONS}
  />
);

interface MonthQuestionProps {
  value: string;
  onChange: (value: string) => void;
}

export const MonthQuestion = ({ value, onChange }: MonthQuestionProps) => (
  <div className="space-y-3">
    <Label>When do you want to travel?</Label>
    <RadioGroup value={value} onValueChange={onChange} className="grid grid-cols-4 gap-2 sm:gap-3">
      {MONTHS.map((m) => (
        <Label
          key={m}
          className={cn(
            'flex items-center justify-center p-2.5 sm:p-3 rounded-lg border cursor-pointer transition-all text-xs sm:text-sm',
            value === m ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          )}
        >
          <RadioGroupItem value={m} className="sr-only" />
          <span>{m}</span>
        </Label>
      ))}
    </RadioGroup>
  </div>
);

interface BudgetQuestionProps {
  value: Budget;
  onChange: (value: Budget) => void;
}

export const BudgetQuestion = ({ value, onChange }: BudgetQuestionProps) => (
  <div className="space-y-3">
    <Label>Budget</Label>
    <RadioGroup value={value} onValueChange={(v) => onChange(v as Budget)} className="grid grid-cols-3 gap-2 sm:gap-3">
      {BUDGET_VALUES.map((b) => (
        <Label
          key={b}
          className={cn(
            'flex flex-col items-center justify-center gap-0.5 p-2.5 sm:p-4 rounded-lg border cursor-pointer transition-all',
            value === b ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          )}
        >
          <RadioGroupItem value={b} className="sr-only" />
          <span className="text-sm sm:text-base font-medium">{b}</span>
        </Label>
      ))}
    </RadioGroup>
  </div>
);
